import { RegionShared } from "@1games-ru/region-shared";
import { Inventory, InventoryItem } from "@1games-ru/region-shared/dist/types/inventory/inventory";
import { debounce } from "lodash";
import { useCallback } from "react";
import useAppDispatch from "@/core/helpers/useAppDispatch";
import useAppSelector from "@/core/helpers/useAppSelector";
import { RpcMpProviderFailedResponse } from "@/core/rpc-mp-provider/responses/rpc-mp-provider-failed-response";
import { RpcMpProviderSuccessResponse } from "@/core/rpc-mp-provider/responses/rpc-mp-provider-success-response";
import RpcMpProvider from "@/core/rpc-mp-provider/rpc-mp-provider";
import { OwnerType } from "@/domains/inventory/constants/ownerType";
import {
  addInventoryItem,
  inventoryMapSelector,
  removeInventoryItem,
  setInventory,
  setTrade,
} from "@/domains/inventory/slice";
import { placeItemsWithMemo } from "@/domains/inventory/helpers/placeItemsWithMemo";
import toast from "@/domains/toasts/helpers/toast";

export default function useTradeManager() {
  const dispatch = useAppDispatch();
  const inventoryMap = useAppSelector(inventoryMapSelector);

  const getTradeState = useCallback(
    async (data: typeof RegionShared.Backend.Requests.TradeGetStateRequest) => {
      const response = await RpcMpProvider.callServer<
        typeof RegionShared.Backend.Requests.TradeGetStateRequest,
        typeof RegionShared.Backend.Responses.TradeGetStateResponse
      >(RegionShared.Backend.Events.Inventory.TradeGetState, {
        tradeId: Number(data.tradeId),
      });

      if (response instanceof RpcMpProviderSuccessResponse) {
        const trade = response.data;

        dispatch(setTrade(trade));
        dispatch(
          setInventory({
            ...trade.initiator.inventory,
            ownerType: OwnerType.Trade,
            items: placeItemsWithMemo(
              trade.initiator.inventory.items as unknown as InventoryItem[],
              trade.initiator.inventory.size.width,
              trade.initiator.inventory.size.height,
            ).map((item) => ({
              ...item,
              inventoryId: trade.initiator.inventory.id,
            })),
          } as unknown as Inventory),
        );
        dispatch(
          setInventory({
            ...trade.recipient.inventory,
            ownerType: OwnerType.Trade,
            items: placeItemsWithMemo(
              trade.recipient.inventory.items as unknown as InventoryItem[],
              trade.recipient.inventory.size.width,
              trade.recipient.inventory.size.height,
            ).map((item) => ({
              ...item,
              inventoryId: trade.recipient.inventory.id,
            })),
          } as unknown as Inventory),
        );
      } else {
        toast.error(response.message);
        console.log({ response });
      }
    },
    [],
  );

  const moveInventoryItem = useCallback(
    async (
      fromInventoryId: number,
      toInventoryId: number,
      item: InventoryItem,
      oldItem?: InventoryItem,
    ) => {
      dispatch(removeInventoryItem({ inventoryId: fromInventoryId, inventoryItemId: item.id }));
      dispatch(
        addInventoryItem({
          inventoryId: toInventoryId,
          inventoryItem: { ...item, inventoryId: toInventoryId },
        }),
      );

      const fromInventory = inventoryMap[fromInventoryId];
      const toInventory = inventoryMap[toInventoryId];
      const availableOwnerTypes = [OwnerType.Bag, OwnerType.Character];

      if (
        availableOwnerTypes.includes(fromInventory.ownerType as OwnerType) &&
        availableOwnerTypes.includes(toInventory.ownerType as OwnerType)
      ) {
        const response = await RpcMpProvider.getInstance().callServer(
          RegionShared.Backend.Events.Inventory.MoveItem,
          {
            fromInventoryId,
            toInventoryId,
            inventoryItemId: item.id,
            position: item.position,
            isRotated: item.isRotated,
          },
        );

        if (response instanceof RpcMpProviderFailedResponse) {
          console.log({ response });
          toast.error(response.message);
          if (oldItem) {
            dispatch(removeInventoryItem({ inventoryId: toInventoryId, inventoryItemId: item.id }));
            dispatch(addInventoryItem({ inventoryId: fromInventoryId, inventoryItem: oldItem }));
          }
        }
      }
    },
    [inventoryMap],
  );

  const addItemToTrade = useCallback(
    async (
      fromInventoryId: number,
      toInventoryId: number,
      data: typeof RegionShared.Backend.Requests.TradeAddItemRequest,
    ) => {
      if (fromInventoryId === toInventoryId) return;

      const response = await RpcMpProvider.getInstance().callServer(
        RegionShared.Backend.Events.Inventory.TradeAddItem,
        data,
      );

      if (response instanceof RpcMpProviderFailedResponse) {
        console.log({ response });
        toast.error(response.message);
      }
    },
    [getTradeState],
  );

  const removeItemFromTrade = useCallback(
    async (
      fromInventoryId: number,
      toInventoryId: number,
      data: typeof RegionShared.Backend.Requests.TradeRemoveItemRequest,
    ) => {
      if (fromInventoryId === toInventoryId) return;

      const response = await RpcMpProvider.getInstance().callServer(
        RegionShared.Backend.Events.Inventory.TradeRemoveItem,
        data,
      );

      if (response instanceof RpcMpProviderFailedResponse) {
        console.log({ response });
        toast.error(response.message);
      }
    },
    [],
  );

  const cancelTrade = useCallback(async (data: { tradeId: number }) => {
    const response = await RpcMpProvider.getInstance().callServer(
      RegionShared.Backend.Events.Inventory.TradeCancel,
      {
        tradeId: Number(data.tradeId),
      },
    );

    if (response instanceof RpcMpProviderFailedResponse) {
      console.log({ response });
      toast.error(response.message);
    }
  }, []);

  const confirmTrade = useCallback(
    async (data: typeof RegionShared.Backend.Requests.TradeSetConfirmedRequest) => {
      const response = await RpcMpProvider.getInstance().callServer(
        RegionShared.Backend.Events.Inventory.TradeSetConfirmed,
        {
          tradeId: Number(data.tradeId),
        },
      );

      if (response instanceof RpcMpProviderFailedResponse) {
        console.log({ response });
        toast.error(response.message);
      }
    },
    [],
  );

  const setCashOffer = useCallback(
    debounce(
      async (data: typeof RegionShared.Backend.Requests.TradeSetCashOfferRequest) => {
        const response = await RpcMpProvider.callServer(
          RegionShared.Backend.Events.Inventory.TradeSetCashOffer,
          {
            tradeId: Number(data.tradeId),
            amount: data.amount,
          },
        );

        if (response instanceof RpcMpProviderFailedResponse) {
          console.log({ response });
          toast.error(response.message);
        }
      },
      500,
      { leading: true, trailing: true },
    ),
    [],
  );

  const setReady = useCallback(
    async (data: typeof RegionShared.Backend.Requests.TradeSetReadyRequest) => {
      const response = await RpcMpProvider.callServer(
        RegionShared.Backend.Events.Inventory.TradeSetReady,
        {
          tradeId: Number(data.tradeId),
          isReady: data.isReady,
        },
      );

      if (response instanceof RpcMpProviderFailedResponse) {
        console.log({ response });
        toast.error(response.message);
      }
    },
    [],
  );

  return {
    addItemToTrade,
    removeItemFromTrade,
    cancelTrade,
    confirmTrade,
    setCashOffer,
    setReady,
    moveInventoryItem,
    getTradeState,
  };
}
